import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { analytics } from '@/hooks/useAnalytics';
/** Currency every price in the site data is stored in (JPY for Japan, KRW for Korea, CNY for China). */
import { SITE, BASE_CURRENCY } from '@/site';
import { CURRENCY_DATA, PRIORITY_CURRENCIES, formatBaseAmount } from './currencyData';

interface ExchangeRates {
  [currency: string]: number;
}

interface CurrencyContextType {
  currency: string;
  setCurrency: (currency: string) => void;
  rates: ExchangeRates | null;
  ratesDate: string | null;
  ratesFetchedAt: string | null;
  isLoading: boolean;
  error: string | null;
  convert: (amountJPY: number, toCurrency?: string) => number;
  formatPrice: (amountJPY: number, toCurrency?: string) => string;
  availableCurrencies: string[];
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

const STORAGE_KEY = SITE.storageKeys.currency;
const RATES_CACHE_KEY = SITE.storageKeys.rates;
const CACHE_DURATION = 6 * 60 * 60 * 1000; // 6 hours

const RATES_API_URL = `https://api.frankfurter.dev/v1/latest?base=${BASE_CURRENCY}`;

interface FrankfurterResponse {
  amount: number;
  base: string;
  date: string;
  rates: ExchangeRates;
}

interface CurrencyProviderProps {
  children: ReactNode;
}

export function CurrencyProvider({ children }: CurrencyProviderProps) {
  // Load saved currency preference
  const [currency, setCurrencyState] = useState<string>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || BASE_CURRENCY;
    } catch {
      return BASE_CURRENCY;
    }
  });

  const [rates, setRates] = useState<ExchangeRates | null>(null);
  const [ratesDate, setRatesDate] = useState<string | null>(null);
  const [ratesFetchedAt, setRatesFetchedAt] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Track previous currency for analytics
  const prevCurrencyRef = useRef(currency);

  // Set currency and save to localStorage
  const setCurrency = useCallback((newCurrency: string) => {
    const prevCurrency = prevCurrencyRef.current;
    setCurrencyState(newCurrency);
    prevCurrencyRef.current = newCurrency;
    
    // Track currency change
    if (prevCurrency !== newCurrency) {
      analytics.currencyChange(prevCurrency, newCurrency);
    }
    
    try {
      localStorage.setItem(STORAGE_KEY, newCurrency);
    } catch {
      // Ignore storage errors
    }
  }, []);

  // Fetch exchange rates (cached for 6 hours in localStorage)
  const fetchRates = useCallback(async () => {
    // Check cache first
    try {
      const cached = localStorage.getItem(RATES_CACHE_KEY);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < CACHE_DURATION) {
          setRates(data.rates);
          setRatesDate(data.date);
          setRatesFetchedAt(data.fetchedAt || new Date(timestamp).toISOString());
          setIsLoading(false);
          return;
        }
      }
    } catch {
      // Ignore cache errors
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(RATES_API_URL);
      if (!response.ok) {
        throw new Error(`Exchange-rate request failed with status ${response.status}`);
      }
      const data = await response.json() as FrankfurterResponse;
      const fetchedAt = new Date().toISOString();

      setRates(data.rates);
      setRatesDate(data.date);
      setRatesFetchedAt(fetchedAt);

      // Cache the rates
      try {
        localStorage.setItem(RATES_CACHE_KEY, JSON.stringify({
          data: {
            rates: data.rates,
            date: data.date,
            fetchedAt,
          },
          timestamp: Date.now(),
        }));
      } catch {
        // Ignore cache errors
      }
    } catch {
      // Rates API down (e.g. 522): stay quiet, keep cached/fallback rates, no retry
      setError('Failed to load exchange rates');

      // Try to use stale cache
      try {
        const cached = localStorage.getItem(RATES_CACHE_KEY);
        if (cached) {
          const { data } = JSON.parse(cached);
          setRates(data.rates);
          setRatesDate(data.date);
          setRatesFetchedAt(data.fetchedAt);
        }
      } catch {
        // No cache available
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRates();
  }, [fetchRates]);

  // A tab left open for a day would otherwise keep yesterday's rates. When the
  // visitor comes back and the cache has expired, fetch again.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const cached = localStorage.getItem(RATES_CACHE_KEY);
        if (cached) {
          const { timestamp } = JSON.parse(cached);
          if (Date.now() - timestamp < CACHE_DURATION) return;
        }
      } catch {
        // Unreadable cache — refetch.
      }
      fetchRates();
    };

    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [fetchRates]);


  // Convert from the site's base currency (amounts are named amountJPY for history) to the target currency
  const convert = useCallback((amountJPY: number, toCurrency?: string): number => {
    const targetCurrency = toCurrency || currency;
    
    if (targetCurrency === BASE_CURRENCY || !rates) {
      return amountJPY;
    }

    const rate = rates[targetCurrency];
    if (!rate) {
      return amountJPY;
    }

    return amountJPY * rate;
  }, [currency, rates]);

  // Format price with currency symbol
  const formatPrice = useCallback((amountJPY: number, toCurrency?: string): string => {
    const targetCurrency = toCurrency || currency;
    const converted = convert(amountJPY, targetCurrency);
    const currencyData = CURRENCY_DATA[targetCurrency];

    if (!currencyData) {
      return formatBaseAmount(amountJPY);
    }

    const formatted = converted.toLocaleString(undefined, {
      minimumFractionDigits: currencyData.decimals,
      maximumFractionDigits: currencyData.decimals,
    });

    return `${currencyData.symbol}${formatted}`;
  }, [currency, convert]);

  // Get available currencies (those we have rates for + the base currency)
  const availableCurrencies = rates 
    ? [BASE_CURRENCY, ...Object.keys(rates).filter(c => CURRENCY_DATA[c] && Number.isFinite(rates[c]))]
    : [BASE_CURRENCY];

  // Sort by priority, then alphabetically
  const sortedCurrencies = availableCurrencies.sort((a, b) => {
    const aIndex = PRIORITY_CURRENCIES.indexOf(a);
    const bIndex = PRIORITY_CURRENCIES.indexOf(b);
    
    if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;
    
    return (CURRENCY_DATA[a]?.country || a).localeCompare(CURRENCY_DATA[b]?.country || b);
  });

  return (
    <CurrencyContext.Provider value={{
      currency,
      setCurrency,
      rates,
      ratesDate,
      ratesFetchedAt,
      isLoading,
      error,
      convert,
      formatPrice,
      availableCurrencies: sortedCurrencies,
    }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
}
